import React, { startTransition } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { CloseOutlined, RightOutlined } from '@ant-design/icons';
import { useSidebar } from '../contexts/SidebarContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import {
  isWorkspaceNavItemActive,
  type WorkspaceNavItem,
} from '../constants/workspaces';
import AdminBrandLogo from '../components/AdminBrandLogo';
import WorkspaceSwitcher from './WorkspaceSwitcher';

const Sidebar: React.FC = () => {
  const { mobileOpen, setMobileOpen } = useSidebar();
  const { pinnedAboveItems, overviewItem, visibleGroups } = useWorkspace();
  const navigate = useNavigate();
  const location = useLocation();

  const handleNavClick = (path: string) => {
    // Keep the active nav highlight responsive while the lazy chunk loads
    // (same idea as web route loading shells).
    setMobileOpen(false);
    startTransition(() => {
      const isAnalyticsNav =
        path.includes('/analytics') && location.pathname.includes('/analytics');
      if (isAnalyticsNav && location.search) {
        navigate({ pathname: path, search: location.search });
      } else {
        navigate(path);
      }
    });
  };

  const renderNavButton = (item: WorkspaceNavItem) => {
    const Icon = item.icon;
    const isActive = isWorkspaceNavItemActive(location.pathname, item.path);
    return (
      <button
        type="button"
        onClick={() => handleNavClick(item.path)}
        className={`group relative flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left transition-all ${
          isActive
            ? 'bg-slate-100 text-slate-900 dark:bg-slate-700/80 dark:text-white'
            : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-700/50 dark:hover:text-white'
        }`}
      >
        {isActive && (
          <span
            className="absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r-full bg-accent-primary dark:bg-[#D9F42B]"
            aria-hidden
          />
        )}
        <span
          className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg transition-colors ${
            isActive
              ? 'bg-blue-50 text-accent-primary dark:bg-slate-700 dark:text-[#D9F42B]'
              : 'text-slate-400 group-hover:text-slate-600 dark:text-slate-500 dark:group-hover:text-slate-300'
          }`}
        >
          <Icon style={{ fontSize: 15 }} />
        </span>
        <span className={`truncate text-[13px] ${isActive ? 'font-semibold' : 'font-medium'}`}>
          {item.label}
        </span>
      </button>
    );
  };

  /**
   * Clickable section header for groups that have a dashboard home.
   * Reads as a group label (uppercase, above children) but is interactive —
   * stronger than static labels, without wrapping the section in a card.
   */
  const renderSectionDashboard = (item: WorkspaceNavItem, label: string) => {
    const Icon = item.icon;
    const isActive = isWorkspaceNavItemActive(location.pathname, item.path);
    return (
      <button
        type="button"
        onClick={() => handleNavClick(item.path)}
        aria-current={isActive ? 'page' : undefined}
        aria-label={`${label} dashboard`}
        className={`group mb-1.5 flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left transition-colors ${
          isActive
            ? 'bg-slate-100 dark:bg-slate-700/50'
            : 'hover:bg-slate-50 dark:hover:bg-slate-700/30'
        }`}
      >
        <span
          className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-md transition-colors ${
            isActive
              ? 'bg-blue-50 text-accent-primary dark:bg-slate-600 dark:text-[#D9F42B]'
              : 'bg-slate-200/80 text-slate-600 group-hover:bg-slate-200 group-hover:text-slate-800 dark:bg-slate-600/60 dark:text-slate-300'
          }`}
        >
          <Icon style={{ fontSize: 12 }} />
        </span>
        <span
          className={`min-w-0 flex-1 truncate text-[11px] font-bold uppercase tracking-[0.08em] ${
            isActive
              ? 'text-accent-primary dark:text-[#D9F42B]'
              : 'text-slate-700 group-hover:text-slate-900 dark:text-slate-200 dark:group-hover:text-white'
          }`}
        >
          {label}
        </span>
        <RightOutlined
          className={`flex-shrink-0 text-[10px] transition-all ${
            isActive
              ? 'text-accent-primary opacity-100 dark:text-[#D9F42B]'
              : 'text-slate-400 opacity-70 group-hover:translate-x-0.5 group-hover:opacity-100 dark:text-slate-500'
          }`}
          aria-hidden
        />
      </button>
    );
  };

  return (
    <aside
      className={`fixed left-0 top-0 z-[9999] flex h-full w-[260px] flex-shrink-0 flex-col border-r border-border bg-sidebar transition-transform md:static md:z-auto md:h-auto md:self-stretch md:pt-[4.5rem] ${
        mobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
      }`}
    >
      {/* Mobile-only brand (desktop logo lives in the full-width header) */}
      <div className="flex h-14 flex-shrink-0 items-center justify-between border-b border-border px-3 md:hidden">
        <AdminBrandLogo className="h-12 w-auto max-w-[220px] object-contain object-left" />
        <button
          type="button"
          onClick={() => setMobileOpen(false)}
          className="rounded-lg p-2 text-foreground-muted transition-colors hover:bg-slate-100 hover:text-foreground dark:hover:bg-slate-800"
          aria-label="Close navigation"
        >
          <CloseOutlined style={{ fontSize: 14 }} />
        </button>
      </div>

      <nav className="flex min-h-0 flex-1 flex-col overflow-y-auto px-3 py-3">
        {/* Workspace switcher lives in the header on desktop; keep it in the drawer on mobile */}
        <div className="md:hidden">
          <WorkspaceSwitcher />
        </div>

        {(pinnedAboveItems.length > 0 || overviewItem) && (
          <div className="mb-2 space-y-0.5">
            {pinnedAboveItems.map((item) => (
              <React.Fragment key={item.id}>{renderNavButton(item)}</React.Fragment>
            ))}
            {overviewItem && renderNavButton(overviewItem)}
          </div>
        )}

        <div className="space-y-5">
          {visibleGroups.map((group) => {
            const hasDashboard = Boolean(group.dashboard);

            if (hasDashboard && group.dashboard) {
              return (
                <div key={group.id}>
                  {renderSectionDashboard(group.dashboard, group.label)}
                  {group.items.length > 0 && (
                    <ul className="ml-3 space-y-0.5 border-l border-slate-200/90 pl-2 dark:border-slate-700/80">
                      {group.items.map((item) => (
                        <li key={item.id}>{renderNavButton(item)}</li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            }

            return (
              <div key={group.id}>
                <p className="mb-1.5 px-3 text-[10px] font-semibold uppercase tracking-[0.1em] text-foreground-muted/80">
                  {group.label}
                </p>
                {group.items.length > 0 && (
                  <ul className="space-y-0.5">
                    {group.items.map((item) => (
                      <li key={item.id}>{renderNavButton(item)}</li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}

          {pinnedAboveItems.length === 0 && !overviewItem && visibleGroups.length === 0 && (
            <p className="px-3 text-sm text-foreground-muted">
              No pages available in this workspace for your permissions.
            </p>
          )}
        </div>
      </nav>
    </aside>
  );
};

export default Sidebar;
